// Small, dependency-free geography shared by the browser and the server: distances, drawn-route
// geometry, display segments and the limits every map feature uses.

/** [longitude, latitude] in degrees, the GeoJSON order. */
export type LonLat = [number, number];

/** Mean Earth radius in metres: the value turf uses, so browser lengths match server ones. */
export const EARTH_RADIUS_M = 6371008.8;

export const MAP_LIMITS = {
  /** A GPX file (browser upload or MCP base64), in bytes. */
  gpxBytes: 10 * 1024 * 1024,
  /** Tracks plus routes in one GPX file: each becomes one PlanA route. */
  gpxTracks: 20,
  /** Track segments (trkseg) in one GPX file. */
  gpxSegments: 500,
  /** Points in one GPX file, all tracks together. */
  gpxPoints: 250_000,
  /** Points in a route's displayLine (all its parts together). */
  displayPoints: 2000,
  /** Waypoints in a drawn route. */
  waypoints: 500,
  /** Routes in one project. */
  routesPerProject: 100,
} as const;

export const CORRIDOR_KM = { default: 2, min: 0.5, max: 20 } as const;

/** Colours given to new routes in turn (readable on OSM tiles). */
export const ROUTE_COLOURS = ['#e11d48', '#2563eb', '#16a34a', '#d97706', '#7c3aed', '#0891b2', '#db2777', '#4d7c0f'] as const;
export const routeColourFor = (existingRoutes: number) => ROUTE_COLOURS[existingRoutes % ROUTE_COLOURS.length];

const rad = (deg: number) => (deg * Math.PI) / 180;

/** Great-circle distance in metres (haversine, the same formula turf's `distance` uses). */
export function haversineM(a: LonLat, b: LonLat): number {
  const dLat = rad(b[1] - a[1]);
  const dLon = rad(b[0] - a[0]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[1])) * Math.cos(rad(b[1])) * Math.sin(dLon / 2) ** 2;
  // Floating point can push h fractionally above 1 for antipodal pairs (e.g. the two poles), which
  // would make sqrt(1 - h) NaN; clamping keeps the result a real distance instead of NaN.
  const clamped = Math.min(1, h);
  return 2 * EARTH_RADIUS_M * Math.atan2(Math.sqrt(clamped), Math.sqrt(1 - clamped));
}

/** Length of a path in metres. */
export function pathLengthM(points: LonLat[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) total += haversineM(points[i - 1], points[i]);
  return total;
}

export const validLonLat = (p: unknown): p is LonLat =>
  Array.isArray(p) && p.length === 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]) && p[0] >= -180 && p[0] <= 180 && p[1] >= -90 && p[1] <= 90;

/**
 * Wraps any finite longitude into (-180, 180]. A single `% 360` isn't enough: JS's `%` keeps the
 * sign of its left operand, so a longitude more than 360° below -180 (e.g. -700, two world copies
 * west) comes out still outside the range instead of wrapping around to it. The double mod — once
 * to land in (-360, 360), then shifted positive before the final mod — handles any finite input,
 * however many world copies away.
 */
export const wrapLon = (lon: number): number => ((((lon + 180) % 360) + 360) % 360) - 180;

/** Rounds to 6 decimals (about 0.1 m) and drops a point identical to the one before it. */
export function cleanPath(points: LonLat[]): LonLat[] {
  const out: LonLat[] = [];
  for (const p of points) {
    const q: LonLat = [Math.round(p[0] * 1e6) / 1e6, Math.round(p[1] * 1e6) / 1e6];
    const last = out[out.length - 1];
    if (!last || last[0] !== q[0] || last[1] !== q[1]) out.push(q);
  }
  return out;
}

/** Drops a point at exactly the same position as the one before it, within one part, without rounding. */
export function dedupConsecutive(part: LonLat[]): LonLat[] {
  const out: LonLat[] = [];
  for (const p of part) {
    const last = out[out.length - 1];
    if (!last || last[0] !== p[0] || last[1] !== p[1]) out.push(p);
  }
  return out;
}

/** FNV-1a, 64 bits, as 16 hex digits. Small inputs only (drawn waypoints); GPX geometry uses SHA-256 on the server. */
export function fnv1a64(text: string): string {
  let h = 0xcbf29ce484222325n;
  for (const byte of new TextEncoder().encode(text)) {
    h ^= BigInt(byte);
    h = (h * 0x100000001b3n) & 0xffffffffffffffffn;
  }
  return h.toString(16).padStart(16, '0');
}

/**
 * What a drawn route stores besides its name and colour: its line, length and geometry hash. A
 * point from Leaflet can land on a neighbouring world copy (e.g. 180.1 or 203.6, not -179.9), and a
 * malformed one can be non-finite; both would otherwise be written straight into a document whose
 * own schema rejects them on the next read, making the card invisible and undeletable. So longitudes
 * are wrapped into [-180, 180] and points that are still invalid are dropped before anything else.
 * Null when fewer than two distinct points survive that (nothing to draw).
 */
export function drawnRouteFields(waypoints: LonLat[]): { waypoints: LonLat[]; displayLine: LonLat[][]; lengthM: number; geometryHash: string } | null {
  const wrapped = waypoints.map((p): LonLat => [wrapLon(p[0]), p[1]]).filter(validLonLat);
  const clean = cleanPath(wrapped).slice(0, MAP_LIMITS.waypoints);
  if (clean.length < 2) return null;
  // A fresh array for displayLine's one part, not the same array object as `waypoints`: the two
  // must be free to be stored, cloned or mutated independently of each other.
  const line: LonLat[] = clean.map((p) => [p[0], p[1]]);
  return {
    waypoints: clean,
    displayLine: [line],
    lengthM: Math.round(pathLengthM(clean)),
    geometryHash: `fnv1a64:${fnv1a64(JSON.stringify(clean))}`,
  };
}

/**
 * How a route is drawn on the map (spec §13): a list of segments, each with its own colour. In v1 a
 * segment is one part of the route's displayLine, all in the route's colour; MTA RI will later
 * split parts by surface or condition. Longitudes are unwrapped so a line crossing the antimeridian
 * (179.9 → -179.9) is drawn as the short hop it is, not across the whole world.
 */
export function routeSegments(route: { colour: string; displayLine: LonLat[][] }): { colour: string; latLngs: [number, number][] }[] {
  return route.displayLine
    .filter((part) => part.length >= 2)
    .map((part) => {
      const latLngs: [number, number][] = [];
      let prevLon = part[0][0];
      let shift = 0;
      for (const [lon, lat] of part) {
        const d = lon + shift - prevLon;
        if (d > 180) shift -= 360;
        else if (d < -180) shift += 360;
        prevLon = lon + shift;
        latLngs.push([lat, prevLon]);
      }
      return { colour: route.colour, latLngs };
    });
}

/** "12.3 km" or "850 m". */
export function formatDistance(m: number): string {
  return Math.round(m) >= 1000 ? `${(m / 1000).toFixed(m >= 99_950 ? 0 : 1)} km` : `${Math.round(m)} m`;
}
